import { Req } from '@payaca/helpers/storeHelper';

import { put, takeEvery, takeLatest } from 'redux-saga/effects';

import { handleAsyncAction } from '../utils';
import {
  createTemplate,
  deleteTemplate,
  getListedTemplatesPage,
  getTemplate,
  updateTemplate,
} from './templatesActions';

import {
  EntityTemplate,
  PublicHydratedEntityTemplate,
  TemplatableEntity,
} from '@payaca/types/entity-templates';
import { ListedTemplatesPage } from '@payaca/types/listedTemplateTypes';
import { PayloadAction } from 'typesafe-actions';
import { getUploadsForEntitySuccess } from '../uploads/uploadsActions';
import { ActionType, SagaConfig } from './templatesTypes';

const templatesSagaCreator = ({
  apiBaseurl,
  getAuthHeader,
  isNativeApp = false,
}: SagaConfig) => {
  const req = Req(`${apiBaseurl}/api`, getAuthHeader, isNativeApp);
  const providerReq = Req(
    `${apiBaseurl}/provider/rest`,
    getAuthHeader,
    isNativeApp
  );

  const handleGetListedTemplatesPage = handleAsyncAction(
    getListedTemplatesPage,
    async (args) => {
      const response = await req.get(
        `/entity-templates/listed-entity-templates?${(
          args.queryParams.entityTypes || []
        )
          .map((et) => `entityTypes[]=${et}`)
          .join('&')}&searchTerm=${
          args.queryParams.searchTerm || ''
        }&includeServicePlanTemplates=${
          args.queryParams.includeServicePlanTemplates || false
        }&pageNumber=${args.queryParams.pageNumber}&pageSize=${
          args.queryParams.pageSize
        }&sortBy=${args.queryParams.sortBy}&sortDirection=${
          args.queryParams.sortDirection
        }`
      );
      return response.json();
    },
    (response, requestData) => {
      requestData.payload.callback?.(response);
    },
    (err, requestData) => {
      requestData.payload.onErrorCallback?.(err);
    }
  );

  const handleGetTemplate = handleAsyncAction(
    getTemplate,
    async (args) => {
      const response = await providerReq.get(`/templates/${args.publicId}`);
      return response.json();
    },
    (_response, requestData) => {
      requestData.payload.callback?.(
        _response as EntityTemplate<TemplatableEntity>
      );
    },
    (_err, requestData) => {
      requestData.payload.onErrorCallback?.(_err);
    }
  );

  const handleUpdateTemplate = handleAsyncAction(
    updateTemplate,
    async (args) => {
      const response = await req.put(
        `/entity-templates/${args.publicId}`,
        args.template
      );
      return response.json();
    },
    (_response, requestData) => {
      requestData.payload.callback?.(_response);
    },
    (err, requestData) => {
      requestData.payload.onErrorCallback?.(err);
    }
  );

  const handleCreateTemplate = handleAsyncAction(
    createTemplate,
    async (args) => {
      const response = await req.post(`/entity-templates`, args.template);
      return response.json();
    },
    (_response, requestData) => {
      requestData.payload.callback?.(_response);
    },
    (err, requestData) => {
      requestData.payload.onErrorCallback?.(err);
    }
  );

  const handleDeleteTemplate = handleAsyncAction(
    deleteTemplate,
    async (args) => {
      const response = await req.del(`/entity-templates/${args.publicId}`);
      return response;
    },
    (_response, requestData) => {
      requestData.payload.callback?.();
    },
    (err, requestData) => {
      requestData.payload.onErrorCallback?.(err);
    }
  );

  function* handleGetListedTemplatesPageSuccess({
    payload,
  }: PayloadAction<
    ActionType.GET_LISTED_TEMPLATES_PAGE_SUCCESS,
    ListedTemplatesPage
  >) {
    const uploads = payload.items.flatMap((item) => item.uploads);
    yield put(getUploadsForEntitySuccess(uploads));
  }

  function* handleGetTemplateSuccess({
    payload,
  }: PayloadAction<
    ActionType.UPDATE_TEMPLATE_SUCCESS | ActionType.CREATE_TEMPLATE_SUCCESS,
    PublicHydratedEntityTemplate<TemplatableEntity>
  >) {
    yield put(getUploadsForEntitySuccess(payload.uploads));
  }

  return function* () {
    yield takeEvery(
      ActionType.GET_LISTED_TEMPLATES_PAGE_REQUEST,
      handleGetListedTemplatesPage
    );
    yield takeLatest(ActionType.UPDATE_TEMPLATE_REQUEST, handleUpdateTemplate);
    yield takeLatest(ActionType.CREATE_TEMPLATE_REQUEST, handleCreateTemplate);
    yield takeLatest(ActionType.DELETE_TEMPLATE_REQUEST, handleDeleteTemplate);
    yield takeEvery(
      ActionType.GET_LISTED_TEMPLATES_PAGE_SUCCESS,
      handleGetListedTemplatesPageSuccess
    );
    yield takeEvery(ActionType.GET_TEMPLATE_REQUEST, handleGetTemplate);
    yield takeEvery(
      ActionType.UPDATE_TEMPLATE_SUCCESS,
      handleGetTemplateSuccess
    );
    yield takeEvery(
      ActionType.CREATE_TEMPLATE_SUCCESS,
      handleGetTemplateSuccess
    );
  };
};

export default templatesSagaCreator;
